import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calculator as CalculatorIcon, Trash2, Copy } from "lucide-react";

export const Calculator = () => {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === "0" ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay("0.");
      setWaitingForOperand(false);
    } else if (display.indexOf(".") === -1) {
      setDisplay(display + ".");
    }
  };

  const clear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = calculate(currentValue, inputValue, operation);

      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue: number, secondValue: number, operation: string) => {
    switch (operation) {
      case "+":
        return firstValue + secondValue;
      case "-":
        return firstValue - secondValue;
      case "×":
        return firstValue * secondValue;
      case "÷":
        return firstValue / secondValue;
      case "=":
        return secondValue;
      default:
        return secondValue;
    }
  };

  const handleOperationClick = (op: string) => {
    if (op === "=") {
      if (operation && previousValue !== null) {
        const inputValue = parseFloat(display);
        const newValue = calculate(previousValue, inputValue, operation);
        setDisplay(String(newValue));
        setPreviousValue(null);
        setOperation(null);
        setWaitingForOperand(true);
      }
    } else {
      performOperation(op);
    }
  };

  const buttons = [
    ["C", "±", "%", "÷"],
    ["7", "8", "9", "×"],
    ["4", "5", "6", "-"],
    ["1", "2", "3", "+"],
    ["0", ".", "="]
  ];

  const getButtonVariant = (button: string) => {
    if (button === "C") return "destructive";
    if (["÷", "×", "-", "+", "="].includes(button)) return "default";
    if (["±", "%"].includes(button)) return "secondary";
    return "outline";
  };

  const getButtonSize = (button: string) => {
    return button === "0" ? "lg" : "default";
  };

  return (
    <div className="w-full space-y-2">
      
      {/* Display */}
      <div className="bg-muted/50 rounded-lg p-2 border border-primary/10">
        <Input
          value={display}
          readOnly
          className="text-right text-base font-mono h-8 border-0 bg-transparent text-foreground"
        />
      </div>

      {/* Clear Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={clear}
        className="w-full h-7 text-xs"
      >
        <Trash2 className="h-3 w-3 mr-1" />
        Clear
      </Button>

      {/* Button Grid */}
      <div className="grid grid-cols-4 gap-1">
        {buttons.flat().map((button) => (
          <Button
            key={button}
            variant={getButtonVariant(button)}
            size="sm"
            className={`h-8 text-xs font-semibold ${
              button === "0" ? "col-span-2" : ""
            } ${button === "=" ? "financial-button" : ""}`}
            onClick={() => {
              if (button === "C") {
                clear();
              } else if (button === "±") {
                setDisplay(String(parseFloat(display) * -1));
              } else if (button === "%") {
                setDisplay(String(parseFloat(display) / 100));
              } else if (button === ".") {
                inputDecimal();
              } else if (["÷", "×", "-", "+", "="].includes(button)) {
                handleOperationClick(button);
              } else {
                inputDigit(button);
              }
            }}
          >
            {button}
          </Button>
        ))}
      </div>

      {/* Quick Actions for Expenses */}
      <div className="mt-2 pt-2 border-t border-border/30">
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs h-7"
          onClick={() => {
            navigator.clipboard.writeText(display);
          }}
        >
          <Copy className="h-3 w-3 mr-1" />
          COPY
        </Button>
      </div>
    </div>
  );
};